import React, { useState, useEffect, useCallback } from 'react';
import {
    getInterviewDefinitions,
    createInterviewDefinition,
    updateInterviewDefinition,
    deleteInterviewDefinition,
    getPatientGroups, // Added
} from '../api/api';
import QuestionEditor from '../components/interview/QuestionEditor';
import './InterviewBuilder.css';

const emptyQuestion = () => ({
    id: '',
    text: '',
    type: 'text',
    options: [],
    validation: { required: true },
    maps_to: null,
});

const emptyDefinition = () => ({
    _id: '',
    title: '',
    description: '',
    questions: [emptyQuestion()],
    metadata: {
        created_by: 'system',
        version: '1.0',
        tags: [],
        priority: 1,
        maps_to_ficha: false,
        target_group_id: '', // Added
        trigger_step: 1,     // Added
    },
});

const InterviewBuilder = () => {
    const [definitions, setDefinitions] = useState([]);
    const [groups, setGroups] = useState([]); // Added
    const [editing, setEditing] = useState(null); // null = list view, object = form view
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [isNew, setIsNew] = useState(false);

    const loadDefinitions = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [defs, grps] = await Promise.all([
                getInterviewDefinitions(),
                getPatientGroups(),
            ]);
            setDefinitions(defs);
            setGroups(grps);
        } catch (err) {
            setError(err.message || 'Error loading definitions');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadDefinitions(); }, [loadDefinitions]);

    const handleNew = () => {
        setEditing(emptyDefinition());
        setIsNew(true);
    };

    const handleEdit = (def) => {
        // Deep clone the definition for editing
        setEditing(JSON.parse(JSON.stringify(def)));
        setIsNew(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm(`¿Eliminar la encuesta "${id}"? Esta acción no se puede deshacer.`)) return;
        try {
            await deleteInterviewDefinition(id);
            await loadDefinitions();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleCancel = () => {
        setEditing(null);
        setIsNew(false);
        setError('');
    };

    const handleSave = async () => {
        if (!editing._id?.trim()) { setError('El ID es obligatorio'); return; }
        if (!editing.title?.trim()) { setError('El título es obligatorio'); return; }
        if (!editing.questions?.length) { setError('Necesitas al menos una pregunta'); return; }

        // Validate every question has id and text
        for (let i = 0; i < editing.questions.length; i++) {
            const q = editing.questions[i];
            if (!q.id?.trim()) { setError(`La pregunta ${i + 1} necesita un ID`); return; }
            if (!q.text?.trim()) { setError(`La pregunta ${i + 1} necesita texto`); return; }
        }

        setSaving(true);
        setError('');
        try {
            if (isNew) {
                await createInterviewDefinition(editing);
            } else {
                await updateInterviewDefinition(editing._id, editing);
            }
            setEditing(null);
            setIsNew(false);
            await loadDefinitions();
        } catch (err) {
            setError(err.message || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    // Question list manipulation
    const addQuestion = () => {
        setEditing({ ...editing, questions: [...editing.questions, emptyQuestion()] });
    };

    const removeQuestion = (idx) => {
        if (editing.questions.length <= 1) return;
        setEditing({ ...editing, questions: editing.questions.filter((_, i) => i !== idx) });
    };

    const updateQuestion = (idx, q) => {
        const questions = [...editing.questions];
        questions[idx] = q;
        setEditing({ ...editing, questions });
    };

    const moveQuestion = (idx, direction) => {
        const newIdx = idx + direction;
        if (newIdx < 0 || newIdx >= editing.questions.length) return;
        const questions = [...editing.questions];
        [questions[idx], questions[newIdx]] = [questions[newIdx], questions[idx]];
        setEditing({ ...editing, questions });
    };

    // --- LIST VIEW ---
    if (!editing) {
        return (
            <div className="interview-builder container">
                <div className="page-header">
                    <h1>📋 Encuestas</h1>
                    <button onClick={handleNew} className="btn-primary">+ Nueva Encuesta</button>
                </div>

                {error && <div className="error-msg">{error}</div>}

                {loading ? (
                    <p className="loading-text">Cargando...</p>
                ) : definitions.length === 0 ? (
                    <div className="empty-state">
                        <p>No hay encuestas creadas todavía.</p>
                        <button onClick={handleNew} className="btn-primary">Crear la primera</button>
                    </div>
                ) : (
                    <table className="definitions-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Título</th>
                                <th>Dirigido a</th>
                                <th>Preguntas</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {definitions.map((def) => (
                                <tr key={def._id}>
                                    <td className="mono">{def._id}</td>
                                    <td>{def.title}</td>
                                    <td>
                                        {def.metadata?.target_group_id ? (
                                            <span className="badge-group">
                                                {groups.find(g => g._id === def.metadata.target_group_id)?.name || def.metadata.target_group_id}
                                                {' '} (Step {def.metadata.trigger_step})
                                            </span>
                                        ) : <span className="text-muted">General</span>}
                                    </td>
                                    <td className="center">{def.questions?.length || 0}</td>
                                    <td className="actions-cell">
                                        <button onClick={() => handleEdit(def)} className="btn-secondary btn-sm">Editar</button>
                                        <button onClick={() => handleDelete(def._id)} className="btn-danger btn-sm">Eliminar</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        );
    }

    // --- FORM VIEW ---
    return (
        <div className="interview-builder container">
            <div className="page-header">
                <h1>{isNew ? '➕ Nueva Encuesta' : `✏️ Editar: ${editing.title || editing._id}`}</h1>
                <div>
                    <button onClick={handleCancel} className="btn-secondary">Cancelar</button>
                    <button onClick={handleSave} className="btn-primary" disabled={saving}>
                        {saving ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </div>

            {error && <div className="error-msg">{error}</div>}

            {/* Metadata fields */}
            <div className="form-section">
                <h2>Datos generales</h2>
                <div className="form-row">
                    <div className="form-group flex-1">
                        <label>ID (único, sin espacios)</label>
                        <input
                            type="text"
                            value={editing._id}
                            onChange={(e) => setEditing({ ...editing, _id: e.target.value.replace(/\s/g, '_').toLowerCase() })}
                            disabled={!isNew}
                            placeholder="censo_inicial"
                        />
                    </div>
                    <div className="form-group flex-2">
                        <label>Título</label>
                        <input
                            type="text"
                            value={editing.title}
                            onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                            placeholder="Censo inicial del paciente"
                        />
                    </div>
                </div>
                <div className="form-group">
                    <label>Descripción</label>
                    <textarea
                        value={editing.description || ''}
                        onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                        placeholder="Encuesta para recopilar datos demográficos y de riesgo..."
                        rows={2}
                    />
                </div>

                {/* NEW LOGIC FIELDS */}
                <div className="form-row">
                    <div className="form-group flex-1">
                        <label>Grupo Objetivo</label>
                        <select
                            value={editing.metadata?.target_group_id || ''}
                            onChange={(e) => setEditing({
                                ...editing,
                                metadata: { ...editing.metadata, target_group_id: e.target.value }
                            })}
                        >
                            <option value="">-- Cualquiera --</option>
                            {groups.map(g => (
                                <option key={g._id} value={g._id}>{g.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group flex-1">
                        <label>Step Mínimo</label>
                        <input
                            type="number"
                            min="1"
                            value={editing.metadata?.trigger_step || 1}
                            onChange={(e) => setEditing({
                                ...editing,
                                metadata: { ...editing.metadata, trigger_step: parseInt(e.target.value) || 1 }
                            })}
                        />
                    </div>
                    <div className="form-group flex-1">
                        <label>Prioridad</label>
                        <input
                            type="number"
                            value={editing.metadata?.priority || 1}
                            onChange={(e) => setEditing({
                                ...editing,
                                metadata: { ...editing.metadata, priority: parseInt(e.target.value) || 1 }
                            })}
                            min={1}
                            max={10}
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                checked={editing.metadata?.maps_to_ficha || false}
                                onChange={(e) => setEditing({
                                    ...editing,
                                    metadata: { ...editing.metadata, maps_to_ficha: e.target.checked }
                                })}
                            />
                            {' '}Mapea a Ficha Oncológica
                        </label>
                    </div>
                    <div className="form-group flex-2">
                        <label>Tags (separados por coma)</label>
                        <input
                            type="text"
                            value={(editing.metadata?.tags || []).join(', ')}
                            onChange={(e) => setEditing({
                                ...editing,
                                metadata: {
                                    ...editing.metadata,
                                    tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                                }
                            })}
                            placeholder="demografico, riesgo, mama"
                        />
                    </div>
                </div>
            </div>

            {/* Questions */}
            <div className="form-section">
                <div className="section-header">
                    <h2>Preguntas ({editing.questions.length})</h2>
                    <button onClick={addQuestion} className="btn-secondary">+ Agregar pregunta</button>
                </div>

                {editing.questions.map((q, idx) => (
                    <QuestionEditor
                        key={idx}
                        question={q}
                        index={idx}
                        onChange={updateQuestion}
                        onRemove={removeQuestion}
                        onMoveUp={() => moveQuestion(idx, -1)}
                        onMoveDown={() => moveQuestion(idx, 1)}
                        isFirst={idx === 0}
                        isLast={idx === editing.questions.length - 1}
                    />
                ))}

                <button onClick={addQuestion} className="btn-secondary btn-full">+ Agregar pregunta al final</button>
            </div>
        </div>
    );
};

export default InterviewBuilder;
